import { useId, useState } from "react";

import PasswordChecklist from "react-password-checklist";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Properties {
  setCurrentPassword: (value: string) => void;
  setPassword: any;
  setPasswordValid: any;
}

/**
 * Eingabeblock für den Passwortwechsel (im Dialog des SecurityPanel).
 *
 * Die Gültigkeit stammt weiterhin ausschließlich von `react-password-checklist`
 * — deshalb kein react-hook-form/zod: eine zweite Validierungsquelle würde von
 * der Regelliste abweichen, die der Nutzerin angezeigt wird. Der Datenfluss
 * (lokaler State + Callbacks nach oben) bleibt unverändert.
 */
export const PasswordChangePanel = (properties: Properties) => {
  const { t } = useTranslation();
  const fieldId = useId();
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState<string>();
  const [passwordRepeat, setPasswordRepeat] = useState<string | undefined>(
    undefined,
  );

  const currentPasswordId = `${fieldId}-current-password`;
  const newPasswordId = `${fieldId}-new-password`;
  const newPasswordRepeatId = `${fieldId}-new-password-repeat`;

  return (
    <div className="flex w-full min-w-0 max-w-2xl flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={currentPasswordId}>
          {t("passwordChangePanel.currentPassword")}
        </Label>
        <Input
          id={currentPasswordId}
          autoFocus
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(event) => {
            setCurrentPassword(event.target.value);
            properties.setCurrentPassword(event.target.value);
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={newPasswordId}>
          {t("common.passwordChangePanel.newPassword")}
        </Label>
        <Input
          id={newPasswordId}
          type="password"
          autoComplete="new-password"
          value={password ?? ""}
          onChange={(event) => {
            setPassword(event.target.value);
            properties.setPassword(event.target.value);
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={newPasswordRepeatId}>
          {t("common.passwordChangePanel.newPasswordRepeat")}
        </Label>
        <Input
          id={newPasswordRepeatId}
          type="password"
          autoComplete="new-password"
          value={passwordRepeat ?? ""}
          onChange={(event) => {
            setPasswordRepeat(event.target.value);
          }}
        />
      </div>
      <PasswordChecklist
        rules={["minLength", "specialChar", "number", "capital", "match"]}
        minLength={5}
        value={password || ""}
        valueAgain={passwordRepeat}
        className="text-sm"
        itemClassName="text-sm"
        validTextColor="hsl(var(--foreground))"
        invalidTextColor="hsl(var(--muted-foreground))"
        iconComponents={{
          ValidIcon: (
            <Check
              aria-hidden="true"
              className="mr-1.5 h-[18px] w-[18px] shrink-0 text-success"
            />
          ),
          InvalidIcon: (
            <X
              aria-hidden="true"
              className="mr-1.5 h-[18px] w-[18px] shrink-0 text-destructive"
            />
          ),
        }}
        messages={{
          minLength: t("common.passwordChangePanel.passwordRuleMinLength"),
          specialChar: t("common.passwordChangePanel.passwordRuleSpecialChar"),
          number: t("common.passwordChangePanel.passwordRuleNumber"),
          capital: t("common.passwordChangePanel.passwordRuleCapital"),
          match: t("common.passwordChangePanel.passwordRuleMatch"),
        }}
        onChange={(valid: boolean) => {
          properties.setPasswordValid(valid);
        }}
      />
    </div>
  );
};
